import type { TypedSupabaseClient } from '../client';
import type { TablesUpdate } from '../../../supabase-client/src/database.types';
import type {
  IClientRepository,
  Client,
  ClientFilters,
  CreateClientInput,
  UpdateClientInput,
} from '../../../interfaces/src';
import { consumeEntityCode, peekEntityCode } from './entity-codes';

export class SupabaseClientRepository implements IClientRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async peekNextCode(): Promise<string> {
    return peekEntityCode(this.supabase, 'client');
  }

  async list(filters?: ClientFilters): Promise<Client[]> {
    let query = this.supabase.from('clients').select('*');

    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,code.ilike.%${filters.search}%`);
    }
    if (filters?.type) {
      query = query.eq('type', filters.type);
    }
    if (filters?.city) {
      query = query.eq('city', filters.city);
    }

    const { data, error } = await query.order('name');
    if (error) throw error;
    return (data ?? []).map(mapClient);
  }

  async getById(id: string): Promise<Client | null> {
    const { data, error } = await this.supabase.from('clients').select('*').eq('id', id).single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapClient(data);
  }

  async create(input: CreateClientInput): Promise<Client> {
    const code = input.code && input.code.trim() !== ''
      ? input.code.trim()
      : await consumeEntityCode(this.supabase, 'client');
    const insertPayload = {
      code,
      name: input.name,
      type: input.type,
      tax_id: input.taxId,
      address: input.address,
      city: input.city,
      phone: input.phone,
      fax: input.fax,
      email: input.email,
      car_registration_number: input.carRegistrationNumber,
      default_discount_pct: input.defaultDiscountPct ?? 0,
      payment_terms_days: input.paymentTermsDays ?? 0,
      notes: input.notes,
      is_default: input.isDefault ?? false,
      pro_client_id: input.proClientId ?? null,
    };
    const { data, error } = await this.supabase
      .from('clients')
      .insert(insertPayload as never)
      .select()
      .single();
    if (error) throw error;
    return mapClient(data);
  }

  async update(id: string, input: UpdateClientInput): Promise<Client> {
    const mapped: TablesUpdate<'clients'> = {};
    if (input.code !== undefined) mapped['code'] = input.code;
    if (input.name !== undefined) mapped['name'] = input.name;
    if (input.type !== undefined) mapped['type'] = input.type;
    if (input.taxId !== undefined) mapped['tax_id'] = input.taxId;
    if (input.address !== undefined) mapped['address'] = input.address;
    if (input.city !== undefined) mapped['city'] = input.city;
    if (input.phone !== undefined) mapped['phone'] = input.phone;
    if (input.fax !== undefined) (mapped as Record<string, unknown>)['fax'] = input.fax;
    if (input.email !== undefined) mapped['email'] = input.email;
    if (input.carRegistrationNumber !== undefined) (mapped as Record<string, unknown>)['car_registration_number'] = input.carRegistrationNumber;
    if (input.defaultDiscountPct !== undefined) mapped['default_discount_pct'] = input.defaultDiscountPct;
    if (input.paymentTermsDays !== undefined) mapped['payment_terms_days'] = input.paymentTermsDays;
    if (input.notes !== undefined) mapped['notes'] = input.notes;
    if (input.proClientId !== undefined) (mapped as Record<string, unknown>)['pro_client_id'] = input.proClientId;

    const { data, error } = await this.supabase
      .from('clients')
      .update(mapped)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapClient(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.from('clients').delete().eq('id', id);
    if (error) throw error;
  }
}

function mapClient(row: Record<string, unknown>): Client {
  return {
    id: row['id'] as string,
    code: row['code'] as string,
    name: row['name'] as string,
    type: row['type'] as Client['type'],
    taxId: (row['tax_id'] as string) ?? null,
    address: (row['address'] as string) ?? null,
    city: (row['city'] as string) ?? null,
    phone: (row['phone'] as string) ?? null,
    fax: (row['fax'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    carRegistrationNumber: (row['car_registration_number'] as string) ?? null,
    defaultDiscountPct: (row['default_discount_pct'] as number) ?? 0,
    paymentTermsDays: (row['payment_terms_days'] as number) ?? 0,
    notes: (row['notes'] as string) ?? null,
    isDefault: (row['is_default'] as boolean) ?? false,
    proClientId: (row['pro_client_id'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}
